import { Card, Table, Typography } from "antd";
import React from "react";
import { useHistory } from "react-router-dom";

import { RegistryDisplayOrganizationName } from "./RegistryDisplayOrgName";
import { useGetPlatformProjectsQuery } from "./store/registryplatformprojectsapi";
import TruncateText from "./TruncateText";
import moment from "moment";

const normalizePlatformProject = (p) => ({
  project_id: p.project_id,
  name: p.name,
  type: p.creditIssuanceRequired === false ? "Quantification Only" : p.type,
  project_period: p.projectPeriodStart && p.projectPeriodEnd
    ? [p.projectPeriodStart, p.projectPeriodEnd]
    : undefined,
  organizationName: p.organizationName,
  status: p.creditIssuanceRequired === false ? "Quantification Only" : p.state,
  region: p.region,
  _id: p._id
});

function RegistryLandingPageView() {
  const { data, isLoading, isError } = useGetPlatformProjectsQuery();

  const history = useHistory();

  const projects = (data?.data ?? []).map(normalizePlatformProject);

  const handleViewDetails = (record) => {
    const projectId = record?.project_id;
    history.push({
      pathname: `/${projectId}`,
    });
  };

  const formatProjectPeriod = (period) => {
    if (!period || period.length < 2) return "-";

    try {
      const startDate = moment(period[0]);
      const endDate = moment(period[1]);

      return `${startDate.format("MMM D, YYYY")} - ${endDate.format(
        "MMM D, YYYY"
      )}`;
    } catch (e) {
      return "Invalid date";
    }
  };
  const columns = [
    {
      title: "Project ID",
      dataIndex: "project_id",
      key: "project_id",
      render: (text) => <TruncateText text={text} length={26} />,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (record) => {
        return <TruncateText text={record} length={34} />;
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 170,
      render: (record) => {
        return (
          <Typography.Text
            style={{
              textTransform: "capitalize",
              fontSize: 16,
              fontFamily: "Lexend",
              whiteSpace: "nowrap",
            }}
          >
            {record}
          </Typography.Text>
        );
      },
    },
    {
      title: "Project Period",
      dataIndex: "project_period",
      key: "project_period",
      width: 220,
      render: (period) => {
        const formattedPeriod = formatProjectPeriod(period);

        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography.Text style={{ fontFamily: "Lexend", fontSize: 14 }}>
              {formattedPeriod}
            </Typography.Text>
          </div>
        );
      },
      sorter: (a, b) => {
        const aDate = a.project_period?.[0] ? new Date(a.project_period[0]) : 0;
        const bDate = b.project_period?.[0] ? new Date(b.project_period[0]) : 0;
        return aDate - bDate;
      },
    },
    {
      key: "Project Developer",
      title: "Organization",
      dataIndex: "organizationName",
      width: 280,
      render: (record) => {
        return <RegistryDisplayOrganizationName name={record} />;
      },
    },
    {
      title: "Project Status",
      key: "status",
      dataIndex: "status",
      sorter: (a, b) => a?.status?.localeCompare(b?.status),
      filters: [
        { text: "Active", value: "Active" },
        { text: "Validator Review", value: "Validator Review" },
        { text: "Public Consultation", value: "Public Consultation" },
        { text: "Eligibility Check", value: "Eligibility Check" },
        { text: "New", value: "New" },
        { text: "Proba Review", value: "ProbaReview" },
        { text: "Quantification Only", value: "Quantification Only" },
      ],
      onFilter: (value, record) => record?.status?.includes(value),
    },
    {
      title: "Region",
      dataIndex: "region",
      key: "region",
      render: (record) => {
        return <TruncateText text={record} />;
      },
    },
  ];
  return (
    <div style={{ height: "80vh" }}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Typography.Title level={2} style={{ fontFamily: "Lexend" }}>
          Proba Registry
        </Typography.Title>
      </div>
      <div>
        <Typography.Paragraph style={{ fontSize: 16, fontFamily: "Lexend" }}>
          The Proba Registry holds certified GHG projects with the data behind
          them: quantified, reported and verified outcomes from agricultural
          value chains that companies can use for Scope 3 claims, alongside
          issued and retired offset credits and removals. Browse the projects
          below.
        </Typography.Paragraph>
        <Typography.Paragraph style={{ fontSize: 16, fontFamily: "Lexend" }}>
          A Proba account is required for any entity wishing to register GHG
          projects or issue, claim, or transfer inset units and offset
          credits. Visit{" "}
          <a href="https://app.proba.earth/">https://app.proba.earth/</a> to
          register your account.
        </Typography.Paragraph>
        <Typography.Paragraph style={{ fontSize: 16, fontFamily: "Lexend" }}>
          For more information on Proba, please visit{" "}
          <a href="https://proba.earth/">https://proba.earth/</a>
        </Typography.Paragraph>
      </div>

      <div>
        <Card className="shadow-card" style={{ borderRadius: 8 }}>
          {isLoading ? (
            <div>Loading...</div>
          ) : isError ? (
            <div>Error fetching data</div>
          ) : (
            <Table
              columns={columns}
              dataSource={projects}
              rowKey={(record) => record._id}
              pagination={{
                pageSizeOptions: ["10", "50", "100"],
                showSizeChanger: true,
              }}
              onRow={(record) => {
                const isQO = record?.type === "Quantification Only";
                return {
                  onClick: () => {
                    if (!isQO) handleViewDetails(record);
                  },
                  style: { cursor: isQO ? "default" : "pointer" },
                };
              }}
            />
          )}
        </Card>
      </div>
    </div>
  );
}

export default RegistryLandingPageView;
